import React, { FC, useMemo, useState } from 'react';
import { Else, If, Then } from 'react-if';
import { useQueryClient } from '@tanstack/react-query';
import cns from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { QUERY_KEY_ME, QUERY_KEY_STORE } from '@zep/apis';
import { useAuth } from '@zep/auth';
import { openZEMChargingPage } from '@zep/common/routes';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { ChevronDownIcon, ClipboardCheckIcon, HeartFillIcon, HeartIcon, QuestionmarkSmallCircleIcon, ZemColorIcon } from '@zep/icons';
import { zusToast } from '@zep/toast';
import { Language } from '@zep/types';
import { Button, Tooltip } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { cn, isMobile } from '@zep/utils';
import { getStoreCategoryIndex, StoreCategoryName, StoreCategoryNumber, StoreQueryKeys, StoreReviewState, useStoreAppSubCategories } from '../../../../consts';
import { usePurchaseStoreItem, useSetLikeStoreItem, useStoreItemDetail } from '../../../../services';
import { ApiCommonResponse } from '../../../../types/GlobalTypes';
import { AssetCommonHashIdProps, AssetStoreDetail } from '../../../../types/StoreTypes';
import S from './AssetSummary.module.scss';
const AssetSummary: FC<AssetCommonHashIdProps> = ({
  assetHashId
}: AssetCommonHashIdProps) => {
  const {
    locale
  } = useRouter();
  const {
    user
  } = useAuth();
  const {
    t
  } = useTranslation(['store']);
  const [isPolicyToggleOn, setIsPolicyToggleOn] = useState(false);
  const assetDetails = useStoreItemDetail(assetHashId, locale as Language, !!user?.isEdu);
  const appCategories = useStoreAppSubCategories();

  /**
   * 에셋의 서브 카테고리에 맞는 라벨을 스태틱 데이터에서 찾아 리턴한다
   */
  const subCategoryLabel = useMemo(() => {
    if (!assetDetails.data) {
      return '';
    }
    const categoryName = appCategories[assetDetails.data.item.subCategory ?? 0].name;
    return categoryName;
  }, [appCategories, assetDetails.data]);
  const handlePolicyToggle = () => {
    setIsPolicyToggleOn(prev => !prev);
  };
  if (!assetDetails.isSuccess) {
    return <></>;
  }
  const isAppInCategories = getStoreCategoryIndex(assetDetails.data.item.category) === StoreCategoryNumber.app;
  const isMinigameInCategories = getStoreCategoryIndex(assetDetails.data.item.category) === StoreCategoryNumber.game;
  const isObjectInCategories = getStoreCategoryIndex(assetDetails.data.item.category) === StoreCategoryNumber.object;
  const assetPrice = assetDetails.data.item.price ?? 0;
  const ownedZem = user?.totalCashMoney ?? 0;
  const giftedZem = user?.giftCashMoney ?? 0;
  const availableZem = ownedZem + giftedZem;
  const giftedZemDescription = `(${t('store:store_gift_zem')} ${giftedZem.toLocaleString()}ZEM)`;
  let editLink = `/store/upload/?type=map&edit=${assetDetails.data?.item.hashId}`;
  if (isObjectInCategories) {
    editLink = `/store/upload/?type=object&edit=${assetDetails.data?.item.hashId}`;
  } else if (isAppInCategories) {
    editLink = `/store/upload/?type=app&edit=${assetDetails.data?.item.hashId}`;
  } else if (isMinigameInCategories) {
    editLink = `/store/upload/?type=game&edit=${assetDetails.data?.item.hashId}`;
  }
  return <div className={cns(S.info_container)} data-sentry-component="AssetSummary" data-sentry-source-file="AssetSummary.tsx">
      <div className={S.asset_info_wrapper}>
        <div className={S.asset_info_inner}>
          {/* 앱일 때 */}
          {isAppInCategories && <img src={assetDetails.data.item.finalImageUrl} alt={assetDetails.data.item.name} className={S.app_summary_image} />}
          <label className={S.info_title}>{assetDetails.data.item.name}</label>
        </div>

        {isAppInCategories && <label className={S.asset_info_description}>
            {assetDetails.data.item.shortDescription}
          </label>}

        <div className={S.user_info_wrapper}>
          {/* FIXME: 추후 유저 img로 변경 */}
          {/* <div className={S.user_info_img}></div> */}
          <label className={S.user_info_nickname}>
            {assetDetails.data.item.player?.finalMarketName}
          </label>
        </div>

        {(isAppInCategories || isMinigameInCategories) && <>
            <div className={S.object_count_wrapper}>
              <div className={S.object_composition}>
                {t('store:store_type')}
              </div>
              <label className={S.object_info_text}>
                {isAppInCategories ? t('store:store_asset_app') : t('store:store_asset_game')}
              </label>
              <Tooltip placement={'bottom-start'} flip={false} tooltipClassName={S.question_mark_panel} title={isAppInCategories && t('store:store_detail_app_desc') || isMinigameInCategories && t('store:store_detail_minigame_desc')}>
                <QuestionmarkSmallCircleIcon id={'appTooltip'} width={20} height={20} fill={Colors.Gray500} />
              </Tooltip>
            </div>
            {isAppInCategories && <div className={S.object_count_wrapper}>
                <div className={S.object_composition}>
                  {t('store:store_category')}
                </div>
                <label className={S.object_info_text}>{subCategoryLabel}</label>
              </div>}
          </>}

        {isObjectInCategories && <div className={S.object_count_wrapper}>
            <div className={S.object_composition}>
              {t('store:store_composition')}
            </div>
            <label className={S.object_info_text}>
              {t('store:store_object_something_count', {
            x: assetDetails.data.item.objects?.length ?? 0
          })}
            </label>
          </div>}

        {!user?.isEdu && <>
            <div className={S.refund_policy_wrapper}>
              <div className={S.refund_title_wrapper} onClick={handlePolicyToggle}>
                <div className={S.refund_title_inner}>
                  <ClipboardCheckIcon width={24} height={24} />
                  {/* 환불 정책 API? 아니면 하드코딩? 추후 변경 사항의 여지때문에 API 호출로 변경해야할 듯*/}
                  <label className={S.refund_policy_title}>
                    {t('store:store_refund_policy')}
                  </label>
                </div>
                <div className={cns(isPolicyToggleOn ? S.toggled_chevron_down : S.toggled_chevron_up)}>
                  <ChevronDownIcon width={30} height={30} />
                </div>
              </div>
              <label className={cns(S.refund_policy_text, {
            [S.refund_policy_active]: !isPolicyToggleOn
          })}>
                {t('store:store_refund_policy_description')}
              </label>
            </div>
            <div className={S.asset_info}>
              <label className={S.asset_price}>
                {!assetPrice ? 'FREE' : <>
                    <ZemColorIcon width={28} height={28} />
                    <span className={S.asset_price_amount}>
                      {assetPrice.toLocaleString()}
                    </span>
                  </>}
              </label>
            </div>
            {user && assetPrice > 0 && <div className={S.zem_balance_summary}>
                <div className={S.zem_balance_total}>
                  <span>{t('store:store_available_zem')}</span>
                  <div className={S.zem_balance_amount}>
                    <strong>{availableZem.toLocaleString()} ZEM</strong>
                    {giftedZem > 0 && <span className={S.zem_balance_breakdown}>
                        {giftedZemDescription}
                      </span>}
                  </div>
                </div>
              </div>}
          </>}
        {/* 모바일에서는 fixed -> bottom: 0으로 들어간다 */}
        <div className={cns(S.asset_info, S.fixed_button_wrapper)}>
          <AssetControlButtons assetDetail={assetDetails.data} editLink={editLink} data-sentry-element="AssetControlButtons" data-sentry-source-file="AssetSummary.tsx" />
        </div>
      </div>
    </div>;
};
const AssetControlButtons = (props: {
  assetDetail: AssetStoreDetail;
  editLink: string;
}) => {
  const {
    assetDetail,
    editLink
  } = props;
  const router = useRouter();
  const {
    user,
    isInitialized
  } = useAuth();
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const {
    t
  } = useTranslation(['store']);
  const purchaseButtonTitleKey = useMemo(() => {
    if (user?.isEdu) {
      return assetDetail.purchased ? 'store_purchase_complete_edu' : 'store_add';
    } else {
      if (assetDetail.purchased) {
        return 'store_purchase_complete';
      } else {
        return assetDetail.item.price === 0 ? 'store_use_it' : 'store_purchase';
      }
    }
  }, [user?.isEdu, assetDetail.purchased, assetDetail.item.price]);

  /**
   * Mutation을 실행하고 나면 에셋을 새로고침하여 정보를 갱신한다
   */
  const mutationResponse = {
    onSuccess: (result: ApiCommonResponse) => {
      if (result) {
        void queryClient.invalidateQueries([StoreQueryKeys.ASSET_DETAIL_GET, assetDetail.item.hashId]);
        void queryClient.invalidateQueries([QUERY_KEY_STORE, user?.isEdu ? 'purchase-edu-map-assets' : 'purchase-map-assets']);
        void queryClient.invalidateQueries(QUERY_KEY_ME);
      }
    },
    onError: (error: Error) => {
      zusToast.error(error.message);
    }
  };
  const purchaseMutation = usePurchaseStoreItem(assetDetail.item.hashId, user?.isEdu, mutationResponse);
  const availableZem = (user?.totalCashMoney ?? 0) + (user?.giftCashMoney ?? 0);

  /**
   * 구매 버튼을 클릭했을 때 호출되는 함수, 무료 에셋은 바로 구매 API를 호출하고 유료 에셋은 결제 화면으로 이동한다.
   */
  const handlePurchase = async () => {
    if (!user) {
      void router.replace('/accounts/signin');
      return;
    }
    const assetPrice = assetDetail.item.price ?? 0;
    const stringifiedCategory = StoreCategoryName[assetDetail.item.category ?? 0];

    // 유료 에셋의 경우 사용자에게 한 번 더 구매할 것인지 팝업을 띄워준다
    if (assetPrice > 0) {
      logger.track('store_detail_buy', {
        type: stringifiedCategory,
        price: assetPrice,
        name: assetDetail.item.name
      });
      // 현재 가지고 있는 젬보다 에셋이 더 비싸면 충전 페이지로 이동한다.
      if (availableZem < assetPrice) {
        openZEMChargingPage();
      } else {
        void dialog.confirm({
          title: t('store:store_confirm_purchase_zem', {
            zemCount: assetPrice.toLocaleString()
          }),
          confirmAction: async () => {
            // eslint-disable-next-line zep/no-tanstack-mutate -- 성공·실패 처리는 mutationResponse 옵션에 있다. mutateAsync 전환은 별건.
            purchaseMutation.mutate();
            logger.track('store_detail_buy_complete', {
              type: stringifiedCategory,
              price: assetPrice,
              name: assetDetail.item.name
            });
          }
        });
      }
    } else {
      // eslint-disable-next-line zep/no-tanstack-mutate -- 성공·실패 처리는 mutationResponse 옵션에 있다. mutateAsync 전환은 별건.
      purchaseMutation.mutate();
      logger.track('store_detail_use', {
        type: stringifiedCategory,
        name: assetDetail.item.name
      });
    }
  };
  const likeMutation = useSetLikeStoreItem(assetDetail.item.hashId, !!user?.isEdu, mutationResponse);

  /**
   * 찜 버튼을 클릭했을 떄 호출되는 함수(이미 찜인 경우 클릭하면 찜이 해제된다)
   */
  const handleLikeToggle = () => {
    if (!user) {
      void router.replace('/accounts/signin');
      return;
    }
    if (assetDetail.item.state !== StoreReviewState.completed && assetDetail.item.state !== StoreReviewState.official) {
      return;
    }
    // eslint-disable-next-line zep/no-tanstack-mutate -- 성공·실패 처리는 mutationResponse 옵션에 있다. mutateAsync 전환은 별건.
    likeMutation.mutate();
    logger.track('store_detail_like', {
      type: assetDetail.data?.isLiked ? 'unlike' : 'like',
      name: assetDetail.data?.item.name
    });
  };
  const isOwnedByUser = user && user.hashId === assetDetail.item.player?.hashId;
  const canPurchase = [StoreReviewState.official, StoreReviewState.completed, StoreReviewState.unofficial].includes(assetDetail.item.state);
  if (!isInitialized) {
    return null;
  }
  if (isOwnedByUser) {
    return <div className="flex w-full gap-2 md:flex-col">
        <Link className={cn(S.buy_button, '!mr-0')} href={editLink} passHref prefetch={false}>
          <Button size={'lg'} variant={'secondary'} className="w-full" disabled={isMobile()}>
            {t('store:store_edit')}
          </Button>
        </Link>
        {canPurchase && <Button size={'lg'} className={cn(S.buy_button, '!mr-0')} disabled={assetDetail.purchased} onClick={!assetDetail.purchased && handlePurchase || undefined}>
            {t(`store:${purchaseButtonTitleKey}`)}
          </Button>}
      </div>;
  }
  if (!canPurchase) {
    return null;
  }
  return <>
      <Button size={'lg'} className={cn(S.buy_button, 'w-full')} disabled={assetDetail.purchased} onClick={!assetDetail.purchased && handlePurchase || undefined} data-sentry-element="Button" data-sentry-source-file="AssetSummary.tsx">
        {t(`store:${purchaseButtonTitleKey}`)}
      </Button>
      <Button size={'lg'} variant={'white'} transparent className={cn('w-12', S.favorite_button)} onClick={handleLikeToggle} data-sentry-element="Button" data-sentry-source-file="AssetSummary.tsx">
        <If condition={assetDetail.isLiked} data-sentry-element="If" data-sentry-source-file="AssetSummary.tsx">
          <Then data-sentry-element="Then" data-sentry-source-file="AssetSummary.tsx">
            <HeartFillIcon width={34} height={34} fill={Colors.PrimaryDefault} data-sentry-element="HeartFillIcon" data-sentry-source-file="AssetSummary.tsx" />
          </Then>
          <Else data-sentry-element="Else" data-sentry-source-file="AssetSummary.tsx">
            <HeartIcon width={34} height={34} fill={Colors.Gray500} data-sentry-element="HeartIcon" data-sentry-source-file="AssetSummary.tsx" />
          </Else>
        </If>
      </Button>
    </>;
};
export default AssetSummary;